import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Template, CartItem } from '@/types/templates';
import { trackAddToCart } from '@/components/FacebookPixel';
import { trackAddToCart as trackAnalyticsAddToCart, trackRemoveFromCart as trackAnalyticsRemoveFromCart } from '@/lib/analytics/tracker';
import { useCartSync, getCartToken, restoreCartByToken } from '@/hooks/useCartSync';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getProductDisplayInfo } from '@/lib/product-utils';

type ReplacementType = 'upgrade' | 'downgrade';

// All products are limited to quantity 1 (templates, services, kits, bundles, add-ons)
// This enforces business rules: one template per cart, one service tier, etc.
const DEFAULT_MAX_QUANTITY = 1;

// Add-on slug patterns (these are NOT templates and should not be subject to bundle exclusivity)
const ADDON_SLUG_PATTERNS = ['addon', 'express', 'round2'];

// Helper to check if a product is a template (not a bundle or addon)
const isTemplateProduct = (template: Template): boolean => {
  const slug = template.slug || '';
  // Not a bundle
  if (template.is_bundle) return false;
  // Not an add-on
  if (ADDON_SLUG_PATTERNS.some(p => slug.toLowerCase().includes(p))) return false;
  return true;
};

interface PendingAddition {
  template: Template;
  productsToRemove: CartItem[];
  replacementType: ReplacementType;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (template: Template) => void;
  addBundleToCart: (template: Template) => void;
  removeFromCart: (templateId: string) => void;
  clearCart: () => void;
  isInCart: (templateId: string) => boolean;
  totalPrice: number;
  itemCount: number;
  // Confirmation dialog state
  pendingAddition: PendingAddition | null;
  confirmAddition: () => void;
  cancelAddition: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('cart');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Failed to parse cart from localStorage:', error);
      localStorage.removeItem('cart');
      return [];
    }
  });
  const [pendingAddition, setPendingAddition] = useState<PendingAddition | null>(null);
  const autoRestoreAttempted = useRef(false);

  // Sanitize cart on load - enforce quantity limits for ALL products
  useEffect(() => {
    setItems(prev => {
      let modified = false;
      const sanitized = prev.map(item => {
        // Get the max quantity for this product type
        const displayInfo = getProductDisplayInfo(
          item.template.slug,
          item.template.is_bundle,
          item.template.name
        );
        const maxQty = displayInfo.maxQuantity || DEFAULT_MAX_QUANTITY;
        
        if (item.quantity > maxQty) {
          modified = true;
          return { ...item, quantity: maxQty };
        }
        return item;
      });
      if (modified) {
        console.debug('Cart sanitized: enforced quantity limits');
      }
      return modified ? sanitized : prev;
    });
  }, []);

  // Save cart to localStorage on changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  // Sync cart to Supabase (debounced, 3 seconds)
  useCartSync(items);

  // Auto-restore cart from server if local cart is empty and we have a cart token
  useEffect(() => {
    const autoRestoreCart = async () => {
      // Only attempt once per session
      if (autoRestoreAttempted.current) return;
      autoRestoreAttempted.current = true;

      // Only restore if cart is empty
      if (items.length > 0) return;

      // Check for cart token in localStorage
      const cartToken = localStorage.getItem('mcs_cart_token');
      if (!cartToken) return;

      try {
        const result = await restoreCartByToken(cartToken);

        if (!result.success || !result.items || result.items.length === 0) {
          return;
        }

        // Fetch full product data for restoration
        const productIds = result.items.map(item => item.id);
        const { data: products, error } = await supabase
          .from('products')
          .select('id, name, slug, description, short_description, price, original_price, features, benefits, problem_solved, image_url, is_bundle, is_active, category_id, replaces_product_ids, created_at, updated_at')
          .in('id', productIds)
          .eq('is_active', true);

        if (error || !products || products.length === 0) {
          return;
        }

        // Restore cart items
        const restoredItems = products.map(product => ({
          template: product as Template,
          quantity: result.items?.find(item => item.id === product.id)?.quantity || 1,
        }));

        setItems(restoredItems);

        // Store promo code if present
        if (result.promoCode) {
          sessionStorage.setItem('recovered_promo_code', result.promoCode);
        }

        console.debug('Cart auto-restored from server');
      } catch (err) {
        console.debug('Auto-restore failed:', err);
      }
    };

    autoRestoreCart();
  }, []); // Only run on mount

  const addToCart = useCallback((template: Template) => {
    // Check if already in cart
    const exists = items.find(item => item.template.id === template.id);
    if (exists) {
      // All products have max quantity of 1
      const displayInfo = getProductDisplayInfo(template.slug, template.is_bundle, template.name);
      toast.info(`Limit 1 per order for ${displayInfo.typeLabel.toLowerCase()}s.`);
      return;
    }

    // TYPE-BASED EXCLUSIVITY CHECK: Template vs Bundle
    // If adding a template and a bundle/kit is in cart, trigger downgrade dialog
    const isNewItemTemplate = isTemplateProduct(template);
    const existingBundle = items.find(item => item.template.is_bundle);

    if (isNewItemTemplate && existingBundle) {
      // Adding a template when bundle is in cart - trigger downgrade dialog
      setPendingAddition({ 
        template, 
        productsToRemove: [existingBundle], 
        replacementType: 'downgrade' 
      });
      return;
    }

    // Check if this product replaces any items in cart
    const replacesIds = template.replaces_product_ids || [];
    const productsToRemove = items.filter(item =>
      replacesIds.includes(item.template.id)
    );

    // Also check if any cart item would be replaced by adding this product
    // (e.g., cart has complete package that "replaces" single templates)
    const cartItemsThatReplaceThis = items.filter(item =>
      (item.template.replaces_product_ids || []).includes(template.id)
    );

    if (productsToRemove.length > 0) {
      // Upgrading: adding a bundle that replaces individual items
      setPendingAddition({ template, productsToRemove, replacementType: 'upgrade' });
    } else if (cartItemsThatReplaceThis.length > 0) {
      // Downgrading: adding individual item when bundle is in cart
      setPendingAddition({ template, productsToRemove: cartItemsThatReplaceThis, replacementType: 'downgrade' });
    } else {
      // Add directly
      setItems(prev => [...prev, { template, quantity: 1 }]);
      // Track Facebook Pixel AddToCart event
      trackAddToCart(template.id, template.name, template.price);
      // Track internal analytics
      trackAnalyticsAddToCart(template.id, template.name, template.price, template.is_bundle);
    }
  }, [items]);

  const confirmAddition = useCallback(() => {
    if (!pendingAddition) return;

    const { template, productsToRemove, replacementType } = pendingAddition;
    const removeIds = productsToRemove.map(item => item.template.id);

    setItems(prev => {
      // Remove the products that will be replaced
      const filtered = prev.filter(item => !removeIds.includes(item.template.id));
      // Add the new product
      return [...filtered, { template, quantity: 1 }];
    });

    // Track Facebook Pixel AddToCart event
    trackAddToCart(template.id, template.name, template.price);
    // Track internal analytics
    trackAnalyticsAddToCart(template.id, template.name, template.price, template.is_bundle);

    // Show user feedback based on replacement type
    if (replacementType === 'upgrade') {
      toast.success('Upgraded to complete kit! Individual templates removed.');
    } else if (replacementType === 'downgrade') {
      toast.info('Complete kit removed. Individual template added.');
    }

    setPendingAddition(null);
  }, [pendingAddition]);

  const cancelAddition = useCallback(() => {
    setPendingAddition(null);
  }, []);

  // Replaces ALL templates when adding the bundle (enforces mutual exclusivity)
  // Keeps non-template items like add-ons
  const addBundleToCart = useCallback((template: Template) => {
    // Check if already in cart
    const exists = items.find(item => item.template.id === template.id);
    if (exists) {
      toast.info('Limit 1 per order for this kit.');
      return;
    }

    setItems(prev => {
      // Remove ALL template products (mutual exclusivity) but keep services, addons, etc.
      const removedTemplates = prev.filter(item => isTemplateProduct(item.template));
      const keptItems = prev.filter(item => !isTemplateProduct(item.template));
      
      // Show feedback if templates were removed
      if (removedTemplates.length > 0) {
        toast.info(`${removedTemplates.length} individual template${removedTemplates.length > 1 ? 's' : ''} removed - included in your kit.`);
      }
      
      // Add the bundle
      return [...keptItems, { template, quantity: 1 }];
    });
    // Track Facebook Pixel AddToCart event for bundle
    trackAddToCart(template.id, template.name, template.price);
    // Track internal analytics
    trackAnalyticsAddToCart(template.id, template.name, template.price, true);
  }, [items]);

  const removeFromCart = (templateId: string) => {
    const itemToRemove = items.find(item => item.template.id === templateId);
    if (itemToRemove) {
      trackAnalyticsRemoveFromCart(templateId, itemToRemove.template.name);
    }
    setItems(prev => prev.filter(item => item.template.id !== templateId));
  };

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback((templateId: string) => {
    return items.some(item => item.template.id === templateId);
  }, [items]);

  const totalPrice = useMemo(
    () => items.reduce((sum, item) => sum + item.template.price * item.quantity, 0),
    [items]
  );
  const itemCount = items.length;

  const cartValue = useMemo(
    () => ({
      items,
      addToCart,
      addBundleToCart,
      removeFromCart,
      clearCart,
      isInCart,
      totalPrice,
      itemCount,
      pendingAddition,
      confirmAddition,
      cancelAddition,
    }),
    [items, addToCart, addBundleToCart, removeFromCart, clearCart, isInCart, totalPrice, itemCount, pendingAddition, confirmAddition, cancelAddition]
  );

  return (
    <CartContext.Provider value={cartValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
